import { useState, useCallback, useEffect, useRef, Component, startTransition } from 'react';
import { canAccess, moduleLockInfo, applyAccess, canSeeHolidays } from './utils/rbac';
import { filterNavSections } from './utils/filterNavSections';
import { navBadgeLabel, sumNavBadges } from './utils/navBadge';
import { hashForLegacyPath } from './utils/legacyPath';
import { userInitials, userJobLine, userDetailLine, userAccessLine } from './utils/identity';
import { initials } from './utils/format';
import { isEmployee } from './utils/employment';
import api from './hooks/useApi';
import useOpenActionsCount from './hooks/useOpenActionsCount';
import useBidAttentionCount from './hooks/useBidAttentionCount';
import useNavCounts from './hooks/useNavCounts';
import { loadPhotos } from './hooks/usePhotos';
import Avatar from './components/shared/Avatar';
import buildInfo from './buildInfo.json';
import { AppContext } from './context';
import { ToastProvider, ToastContainer } from './components/ui/Toast';
import Modal from './components/shared/Modal';

// Static imports for all page components.
// We deliberately avoid React.lazy() / dynamic imports here because Vite's
// chunk-splitting combined with React 18 concurrent-mode scheduling can create
// a race where a lazy chunk resolves after React has already scheduled a render,
// leaving the component type undefined and causing "n is not a function" crashes.
// For an internal business app, loading all pages upfront is the right trade-off.
import Dashboard         from './components/dashboard/index';
import TrackerPage       from './components/commercial/TrackerPage';
import MySowsPage        from './components/commercial/MySowsPage';
import SOWDetailPage     from './components/commercial/SOWDetailPage';
import SowOverviewPage    from './components/commercial/SowOverviewPage';
import MilestonesPage    from './components/commercial/MilestonesPage';
import ProjectUpdatePage from './components/delivery/ProjectUpdatePage';
import DeliveryCockpitPage from './components/delivery/DeliveryCockpitPage';
import PmoPortfolioPage   from './components/delivery/PmoPortfolioPage';
import PlanPortfolioPage  from './components/delivery/PlanPortfolioPage';
import RiskRegisterPage    from './components/delivery/RiskRegisterPage';
import SltRegisterPage     from './components/delivery/SltRegisterPage';
import ChangeRegisterPage  from './components/delivery/ChangeRegisterPage';
import ApplicationCataloguePage from './components/delivery/ApplicationCataloguePage';
import EcrTrackerPage from './components/delivery/EcrTrackerPage';
import SurveysPage from './components/delivery/SurveysPage';
import RepeatablePage    from './components/delivery/RepeatablePage';
import ForecastPage      from './components/delivery/ForecastPage';
import WeekPlannerPage   from './components/delivery/WeekPlannerPage';
import PlowPage          from './components/delivery/PlowPage';
import PmAllocationPage  from './components/delivery/PmAllocationPage';
import POPage              from './components/procurement/POPage';
import InventoryPage       from './components/procurement/InventoryPage';
import InventoryManagerDashboard from './components/procurement/InventoryManagerDashboard';
import HardwareRequestPage from './components/procurement/HardwareRequestPage';
import AdhocPurchasesReviewPage from './components/procurement/AdhocPurchasesReviewPage';
import ClientOrdersPage    from './components/procurement/ClientOrdersPage';
import PortalCataloguePage from './components/commercial/PortalCataloguePage';
import PeoplePage        from './components/people/PeoplePage';
import RateBandsPage     from './components/people/RateBandsPage';
import ClearanceRenewalsPage from './components/people/ClearanceRenewalsPage';
import LaptopsPage from './components/people/LaptopsPage';
import PeoplePlanPage from './components/people/PeoplePlanPage';
import OrgChartPage      from './components/people/OrgChartPage';
import OnboardingPage    from './components/people/OnboardingPage';
import TalentAcquisitionPage from './components/people/TalentAcquisitionPage';
import InterviewFeedbackPage from './components/people/InterviewFeedbackPage';
import HolidaysPage      from './components/people/HolidaysPage';
import LeaveCalendarPage from './components/people/LeaveCalendarPage';
import OvertimePage      from './components/people/OvertimePage';
import ApprovalsPage     from './components/people/ApprovalsPage';
import WhatsChanged      from './components/shared/WhatsChanged';
import OvertimeLedgerPage from './components/finance/OvertimeLedgerPage';
import { MyContactPage, MyLeavePage, MyBenefitsPage, MyAllowancesPage } from './components/people/MyInfoPage';
import MyTasksPage       from './components/people/MyTasksPage';
import TaskOversightPage  from './components/people/TaskOversightPage';
import ActionOversightPage from './components/people/ActionOversightPage';
import BudgetPage          from './components/finance/BudgetPage';
import ExecutiveSummaryPage from './components/finance/ExecutiveSummaryPage';
import ProfitabilityPage   from './components/finance/ProfitabilityPage';
import AuditPage           from './components/finance/AuditPage';
import ExpectedIncomePage  from './components/finance/ExpectedIncomePage';
import RemittancePage      from './components/finance/RemittancePage';
import PayrollPage         from './components/finance/PayrollPage';
import AdminPage         from './components/admin/AdminPage';
import ClientsPage       from './components/admin/ClientsPage';
import M365LicensesPage  from './components/admin/M365LicensesPage';
import BenefitsAdminPage from './components/admin/BenefitsAdminPage';
import ProductCatalogPage from './components/admin/ProductCatalogPage';
import PlanTemplatesPage  from './components/admin/ProjectPlanTemplatesPage';
import SOWTemplatePage    from './components/admin/SOWTemplatePage';
import AiKnowledgePage    from './components/admin/AiKnowledgePage';
import SupplierPage       from './components/admin/SupplierPage';
import OverheadsPage           from './components/admin/OverheadsPage';
import AccessPage             from './components/admin/AccessPage';
import RateCardPage       from './components/admin/RateCardPage';
import TimesheetPage      from './components/finance/TimesheetPage';
import ScanPage           from './components/procurement/ScanPage';
import BulkScanPage       from './components/procurement/BulkScanPage';
import { isBulkScanActive } from './utils/bulkScan';
import { StockTakeScanPage, StockTakeScanMode } from './components/procurement/InventoryPage';
import EmailInboxPage     from './components/procurement/EmailInboxPage';
import VatReclaimPage     from './components/finance/VatReclaimPage';
import ContractorDashboardPage from './components/finance/ContractorDashboardPage';
import CasualInvoicesPage from './components/finance/CasualInvoicesPage';
import CasualPortal       from './components/casual/CasualPortal';
import { ClientPortal }   from './components/casual/CasualPortal';
import ClientPortalShell  from './components/portal/ClientPortalShell';
import MyBookingsPage       from './components/bookings/MyBookingsPage';
import FloorPlanDisplayPage from './components/bookings/FloorPlanDisplayPage';
import FloorPlanDisplayShell from './components/bookings/FloorPlanDisplayShell';
import ResourceManagementPage from './components/bookings/ResourceManagementPage';
import PmoDashboardPage    from './components/delivery/PmoDashboardPage';
import DeliveryAnalyticsPage from './components/delivery/DeliveryAnalyticsPage';
import PmAchievementPage  from './components/delivery/PmAchievementPage';
import SuggestionsAdminPage   from './components/admin/SuggestionsAdminPage';
import SentEmailsPage         from './components/admin/SentEmailsPage';
import ResourcesDashboardPage from './components/admin/ResourcesDashboardPage';
import DeliveryAddressPage  from './components/admin/DeliveryAddressPage';
import TimeOverviewPage     from './components/admin/TimeOverviewPage';
import HoursAnalysisPage    from './components/admin/HoursAnalysisPage';
import TimeEntryAuditPage   from './components/admin/TimeEntryAuditPage';
import ImportPage           from './components/admin/ImportPage';
import SowDocumentBackfillPage from './components/admin/SowDocumentBackfillPage';
import SowDocumentDuplicatesPage from './components/admin/SowDocumentDuplicatesPage';
import BackupsPage from './components/admin/BackupsPage';
import ZohoCutoverImportPage from './components/admin/ZohoCutoverImportPage';
import PriceBookImportPage from './components/admin/PriceBookImportPage';
import LeaveImportPage from './components/admin/LeaveImportPage';
import MidwichPriceSyncPage from './components/admin/MidwichPriceSyncPage';
import ProductSaleHistoryPage from './components/admin/ProductSaleHistoryPage';
import SowLegacyExtractionPage from './components/admin/SowLegacyExtractionPage';
import NotificationBell    from './components/notifications/NotificationBell';
import GlobalSearch        from './components/shared/GlobalSearch';
import SuggestionsPanel    from './components/shared/SuggestionsPanel';
import QuickTimeLog        from './components/shared/QuickTimeLog';
import SupportSowsPage from './components/commercial/SupportSowsPage';
import CrmDashboardPage from './components/crm/CrmDashboardPage';
import CrmContactsPage  from './components/crm/CrmContactsPage';
import BidTrackerPage       from './components/crm/BidTrackerPage';
import BidLibraryPage       from './components/crm/BidLibraryPage';
import SagePage             from './components/finance/SagePage';
import CardSpendPage        from './components/finance/CardSpendPage';
import BidDetailPage        from './components/crm/BidDetailPage';
import SOWValidationPage    from './components/commercial/SOWValidationPage';
import ReconciliationPage    from './components/commercial/ReconciliationPage';
import DraftSowDashboard   from './components/commercial/DraftSowDashboard';
import ServiceExpiryPage   from './components/commercial/ServiceExpiryPage';
import POSpendPage           from './components/finance/POSpendPage';
import OutgoingInvoicesPage  from './components/finance/OutgoingInvoicesPage';
import AIUsageReportPage  from './components/admin/AIUsageReportPage';
import AppAdoptionPage    from './components/admin/AppAdoptionPage';
import MyTimePage        from './components/my-space/MyTimePage';
import MyListPage        from './components/my-space/MyListPage';
import QmsLibraryPage   from './components/quality/QmsLibraryPage';
import QmsAdminPage     from './components/quality/QmsAdminPage';

// ── Persisted state (survives page refresh via localStorage) ─────────────────
function usePersistedState(key, init) {
  const [state, setState] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : init; } catch { return init; }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(state)); } catch { /* unavailable */ }
  }, [state]);
  return [state, setState];
}

const DENSITY_ZOOM = { compact: 0.85, normal: 1.15, comfortable: 1.28 };

// Error boundary — catches "n is not a function" and similar React render errors
// so a single bad component can't take down the whole app.
class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null, resetKey: props.resetKey, retries: 0 }; this._retryTimer = null; }
  // Reset error state when the page changes without unmounting/remounting the boundary.
  // Using key={page} would remount the boundary on every navigation which in React 18
  // concurrent mode causes the new boundary instance to not be active before its children
  // render, letting errors escape to RootErrorBoundary.
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) return { error: null, resetKey: props.resetKey, retries: 0 };
    return null;
  }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    const page = this.props.resetKey;
    // The "n is not a function" failure is a transient module/concurrent-mode
    // race (a chunk resolving after React scheduled a render). A refresh fixes
    // it, and so does simply re-rendering — so auto-retry once before ever
    // showing the user the manual fallback. Only the *persistent* case (an error
    // that survives the retry) is a genuine failure worth a red console.error;
    // logging the recoverable case as an error is what spams the console.
    if (this.state.retries < 1) {
      console.warn(`[PageErrorBoundary] transient render error on "${page}" — auto-recovering:`, error?.message || error);
      this._retryTimer = setTimeout(() => this.setState(s => ({ error: null, retries: s.retries + 1 })), 60);
    } else {
      console.error(`[PageErrorBoundary] render error on "${page}" persisted after retry:`, error, info);
    }
  }
  componentWillUnmount() { if (this._retryTimer) clearTimeout(this._retryTimer); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, fontFamily: 'Montserrat, sans-serif' }}>
          <div style={{ fontWeight: 700, color: '#DC2626', marginBottom: 8, fontSize: 13 }}>Page render error</div>
          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 12 }}>{String(this.state.error)}</div>
          <button
            style={{ padding: '6px 16px', background: '#1B2238', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
            onClick={() => this.setState({ error: null, retries: 0 })}
          >Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Module locked gate — shown when a page's module is switched off in Admin.
// The menu item stays visible (so users see what's coming) but the component
// itself is gated until the rollout enables it.
// A count badge in the sidebar: on a badged item, and summed onto a collapsed
// section or group so folding the menu away does not hide a waiting action.
function NavBadge({ counts, noun = 'action', title }) {
  const label = navBadgeLabel(counts?.open);
  if (!label) return null;
  return (
    <span
      title={title || (counts.overdue > 0
        ? `${counts.open} open, ${counts.overdue} overdue`
        : `${counts.open} open ${noun}${counts.open === 1 ? '' : 's'}`)}
      style={{
        marginLeft: 'auto', minWidth: 15, padding: '0 4px', borderRadius: 999,
        fontSize: 9, fontWeight: 700, lineHeight: '15px', textAlign: 'center',
        // The sidebar is navy in both themes, so these are fixed
        // brand colours rather than theme-aware utilities. Red is
        // reserved for a queue that has gone past its due date.
        background: counts.overdue > 0 ? '#EF4444' : '#E5B87A',
        color: counts.overdue > 0 ? '#FFFFFF' : '#0D1D32',
      }}
    >
      {label}
    </span>
  );
}

function ModuleLockedPanel({ info }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', padding: 24 }}>
      <div style={{ width: 56, height: 56, borderRadius: 14, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', marginBottom: 16 }}>
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1B2238', fontFamily: 'Montserrat, sans-serif' }}>{info.label} — coming soon</div>
      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8, maxWidth: 420, lineHeight: 1.6 }}>
        {info.message || 'This area is being prepared and will be switched on shortly. The menu is visible so you can see what’s coming.'}
      </div>
    </div>
  );
}

// Shell boundary — wraps header/sidebar widgets that are outside the page boundary.
// Silently swallows crashes there so a notification-bell or search hiccup never
// brings down the whole app frame.
class ShellErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[ShellErrorBoundary]', error, info); }
  render() {
    if (this.state.error) return this.props.fallback ?? null;
    return this.props.children;
  }
}

// Detect resource worker portal (separate auth, no M365)
const IS_RESOURCE_PORTAL = window.location.pathname.startsWith('/resource');
// Detect client contact portal (separate username/password auth, no M365)
const IS_CLIENT_PORTAL   = window.location.pathname.startsWith('/client-portal');
// The tablet at the office entrance (own username/password, no M365)
const IS_DISPLAY_SURFACE = window.location.pathname.startsWith('/display');
// Any of them. None uses the staff app's hash navigation, so anything
// that reads or writes the hash has to leave them alone.
const IS_PORTAL_SURFACE  = IS_RESOURCE_PORTAL || IS_CLIENT_PORTAL || IS_DISPLAY_SURFACE;

// Detect QR scan mode: ?scan=AV-INV-00001
const SCAN_TAG = new URLSearchParams(window.location.search).get('scan');
// Detect stock-take scan mode: ?stocktake=<id> (&review=1 to leave scan mode)
const STOCKTAKE_ID = new URLSearchParams(window.location.search).get('stocktake');
const STOCKTAKE_REVIEW = new URLSearchParams(window.location.search).get('review') === '1';
// A stock take active on this device pulls item QR scans into the count
const ACTIVE_STOCKTAKE = (() => { try { return localStorage.getItem('activeStockTake'); } catch (e) { return null; } })();
// Bulk scan: ?bulkscan=1 opens the basket screen; while a basket is active on
// this device, item QR scans are collected into it instead of opening the item.
const BULK_SCAN_MODE = new URLSearchParams(window.location.search).get('bulkscan') === '1';
const BULK_SCAN_ACTIVE = (() => { try { return isBulkScanActive(localStorage); } catch (e) { return false; } })();
// Wall display: ?display=floor-plan (&location=<id>) shows one office's plan full screen,
// for a tablet at the entrance. Signed in like any other browser; gated by its page grant.
const DISPLAY_VIEW = new URLSearchParams(window.location.search).get('display');
const DISPLAY_LOCATION = new URLSearchParams(window.location.search).get('location');

// AV Logo — white PNG variant (AltonValley-05.png) on navy sidebar
const AVLogo = () => (
  <img
    src="/av-logo-white.png"
    alt="Alton Valley"
    style={{ objectFit: 'contain', height: 44, width: 'auto', maxWidth: 140 }}
  />
);

// Navigation icons (all sized by parent CSS)
const Icons = {
  Dashboard: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  Pipeline: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  Tracker: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>,
  Milestones: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  SupportSows: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Health: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  Repeatable: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  PO: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>,
  Inventory: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>,
  People: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  Holiday: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/></svg>,
  OrgChart: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="2" width="6" height="4" rx="1"/><rect x="2" y="18" width="6" height="4" rx="1"/><rect x="9" y="18" width="6" height="4" rx="1"/><rect x="16" y="18" width="6" height="4" rx="1"/><path d="M5 18v-4h14v4M12 6v8"/></svg>,
  Forecast: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  Budget: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  Profit: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Audit: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><circle cx="12" cy="14" r="2"/></svg>,
  Income: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>,
  Admin: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/><path d="M18 14l2 2 4-4" strokeWidth="2.5"/></svg>,
  Catalog: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M7 8h10M7 12h6"/></svg>,
  PlanTemplate: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="4" rx="1"/><rect x="3" y="10" width="11" height="3" rx="1"/><rect x="3" y="16" width="8" height="3" rx="1"/><path d="M17 13l2 2 4-4"/></svg>,
  MyInfo: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  MyTasks: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/><path d="M8 15h4m2 0h2"/><circle cx="7" cy="7.5" r="0.5" fill="currentColor"/></svg>,
  Supplier:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  HWRequest:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><path d="M12 12v4m-2-2h4"/></svg>,
  Timesheet:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  Payroll:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M7 15h2m4 0h4"/><circle cx="12" cy="15" r="1" fill="currentColor"/></svg>,
  Overheads:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>,
  RateCard:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/><path d="M7 15h2m4 0h4"/></svg>,
  Clients:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><path d="M12 12h.01M8 16h8"/></svg>,
  Benefits:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
  Overtime:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/><path d="M17 20l2 2M7 20l-2 2"/></svg>,
  Standby:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
  Mail:       () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  Onboarding: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8l2 2 4-4" strokeWidth="2.5"/></svg>,
  Talent:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="7" r="4"/><path d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2"/><path d="M17 3l1.5 3 3.5.5-2.5 2.5.5 3.5-3-1.5-3 1.5.5-3.5L12 6.5l3.5-.5z" strokeWidth="1.5"/></svg>,
  DeskMove:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="10" height="7" rx="1"/><rect x="12" y="7" width="10" height="7" rx="1"/><path d="M7 14v3M17 14v3M4 17h6M14 17h6"/><path d="M9 10.5h6"/></svg>,
  DeskBook:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="13" rx="2"/><path d="M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2"/><path d="M8 12h8M8 16h5"/><circle cx="17" cy="16" r="1" fill="currentColor"/></svg>,
  Bookings:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/></svg>,
  DeskAdmin:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="13" rx="2"/><path d="M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2"/><path d="M8 12h5"/><path d="M15 12l2 2 4-4" strokeWidth="2.5"/></svg>,
  Lightbulb:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.62-1.41 4.91-3.5 6.21V17a1 1 0 01-1 1h-5a1 1 0 01-1-1v-1.79C6.41 13.91 5 11.62 5 9a7 7 0 017-7z"/></svg>,
  Suggestions: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><path d="M12 7v5M12 15v.01" strokeLinecap="round"/></svg>,
  WeekPlanner: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/></svg>,
  M365:        () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M7 7h4v4H7zM13 7h4v4h-4zM7 13h4M13 13h4"/></svg>,
  Allocation:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/><path d="M16 11h6m-3-3v6"/></svg>,
  PmoDash:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="4" rx="1"/><rect x="3" y="10" width="18" height="4" rx="1"/><rect x="3" y="17" width="11" height="4" rx="1"/><circle cx="19" cy="19" r="2" fill="currentColor" strokeWidth="0"/><path d="M19 17v0M19 17.5v1" strokeWidth="0"/></svg>,
  Trophy:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9H4a2 2 0 01-2-2V5h4M18 9h2a2 2 0 002-2V5h-4"/><path d="M6 5h12v7a6 6 0 01-12 0V5z"/><path d="M12 18v3M8 21h8"/></svg>,
  ReAug:       () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  Risk:        () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  Casual:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/><path d="M20 8l2-2-2-2"/></svg>,
  CasualInv:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="18" rx="2"/><path d="M7 8h10M7 12h10M7 16h6"/><path d="M17 16l2 2 3-3"/></svg>,
  Import:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>,
  CrmDash:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"/></svg>,
  Leads:       () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  Contacts:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  BidTracker:  () =><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  Moon:        () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>,
  Sun:         () => <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  MyTime:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/><path d="M8 21h8" strokeLinecap="round"/></svg>,
  MyList:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5" strokeLinecap="round"/></svg>,
  Quality:     (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  Renew:       () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0115-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 01-15 6.7L3 16"/></svg>,
  Laptop:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="11" rx="1"/><path d="M2 19h20l-2-4H4z"/></svg>,
  AppCatalogue: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
};

// en-GB with base sensitivity, so a label such as "Ext. Resources Dashboard"
// sorts by its words rather than by its punctuation.
const byLabel = (a, b) => String(a.label).localeCompare(String(b.label), 'en-GB', { sensitivity: 'base' });

// Whether the Approvals entry is worth offering at all. The page grant lets
// every internal role open the shell, because the tabs inside it are what
// decide anything; without this an engineer who manages nobody would be given
// a menu entry that opens on "nothing is waiting on you". Hiring is left out
// deliberately: knowing whether somebody is a named approver means asking the
// server, and a menu cannot wait for that.
const canSeeAnyApproval = (u) =>
  canSeeHolidays(u) || canAccess('overtime');

// Navigation structure
const NAV_SECTIONS = [
  { section: 'My Space', items: [
    // Matt, 16 September 2026: the personal pages say whose they are ("My
    // Actions", not "Actions"), so they read the same way as My Info and My
    // Time do, and Bookings names what is booked.
    { id: 'dashboard',         icon: Icons.Dashboard, label: 'My Dashboard' },
    // My Info was one page with four tabs, which meant the sidebar could not
    // say which of them you were on and nothing could link to one. They are
    // pages now, kept together under a group rather than scattered through an
    // alphabetical My Space, so "my information" is still one recognisable
    // thing and the section stays short when the group is shut. My Company
    // Card and My Time joined the group later: they are the person's own
    // records too, which is what the group holds.
    { group: 'my-info', icon: Icons.MyInfo, label: 'My Info', children: [
      { id: 'my-contact',      icon: Icons.MyInfo,    label: 'My Contact Details' },
      { id: 'my-leave',        icon: Icons.Holiday,   label: 'My Leave & Holidays' },
      // Employees, permanent and fixed-term, have a benefits allocation; a
      // contractor's tab was never rendered, so their page is never offered.
      { id: 'my-benefits',     icon: Icons.Benefits,  label: 'My Benefits',
        when: (u) => isEmployee(u?.employment_type) },
      { id: 'my-allowances',   icon: Icons.Overtime,  label: 'My Overtime' },
      { id: 'my-card-spend',   icon: Icons.RateCard,  label: 'My Company Card' },
      { id: 'my-time',         icon: Icons.MyTime,    label: 'My Time' },
    ]},
    { id: 'my-tasks',          icon: Icons.MyTasks,   label: 'My Actions' },
    { id: 'my-list',           icon: Icons.MyList,    label: 'My List' },
    { id: 'my-bookings',       icon: Icons.Bookings,  label: 'Desk & Van Bookings' },
    // What somebody looks up or acts on about the company and their own team:
    // who reports to whom, who is off, and the two queues waiting on them. The
    // approval queues were on opposite sides of the menu, overtime here and
    // leave under People, which is no way to find out what needs doing. They sit with the personal pages instead of under People,
    // grouped so My Space stays short. Who may open each is still decided in
    // Roles & Permissions, so Approval Requests reaching My Space does not put
    // it in front of anybody who approves nothing.
    { group: 'organisation', icon: Icons.OrgChart, label: 'Organisation', children: [
      { id: 'org-chart',       icon: Icons.OrgChart,  label: 'Org Chart' },
      // Leave and overtime were two entries here, which is two places to look
      // for the same job. One page, a tab per kind, and each tab still asks
      // its own question about who may see it.
      { id: 'approvals',       icon: Icons.Overtime,  label: 'Approvals', when: canSeeAnyApproval },
      { id: 'leave-calendar',  icon: Icons.Holiday,   label: 'Leave Calendar' },
    ]},
  ]},
  // Item 26. Inventory, hardware requests and the quality library are company
  // resources rather than anybody's own space, so they sit in a section of
  // their own instead of under My Space.
  { section: 'Resources', items: [
    { id: 'inventory',         icon: Icons.Inventory, label: 'Inventory' },
    { id: 'hardware-requests', icon: Icons.HWRequest, label: 'HW Requests' },
    { id: 'qms-library',       icon: Icons.Quality,   label: 'Quality Docs' },
  ]},
  { section: 'Commercial', items: [
    { id: 'bid-tracker', icon: Icons.BidTracker, label: 'Pipeline Tracker' },
    { id: 'bid-library', icon: Icons.Quality,    label: 'Content Library' },
    { id: 'tracker',    icon: Icons.Tracker,    label: 'Opp. Tracker' },
    { id: 'draft-sows', icon: Icons.Holiday,    label: 'Draft SOWs' },
    { id: 'milestones', icon: Icons.Milestones, label: 'Milestones' },
    { id: 'support-sows', icon: Icons.SupportSows, label: 'Support SOWs' },
    { id: 'sow-validation', icon: Icons.Risk, label: 'SOW Validation' },
    { id: 'sow-reconciliation', icon: Icons.Milestones, label: 'Remaining Reconciliation' },
    { id: 'service-expiry', icon: Icons.Renew,      label: 'Service Renewals' },
    { id: 'portal-catalogue', icon: Icons.Catalog,  label: 'Portal Catalogue' },
  ]},
  { section: 'Delivery', items: [
    { id: 'pmo-dashboard',    icon: Icons.PmoDash,  label: 'PMO Dashboard' },
    { id: 'delivery-analytics', icon: Icons.PmoDash, label: 'Delivery Analytics' },
    { id: 'pmo-portfolio',    icon: Icons.PmoDash,  label: 'Portfolio' },
    { id: 'plan-portfolio',   icon: Icons.PmoDash,  label: 'Plan Portfolio' },
    { id: 'pm-achievements',  icon: Icons.Trophy,   label: 'Achievements' },
    { id: 'delivery-cockpit', icon: Icons.Health,   label: 'Delivery Cockpit' },
    { id: 'project-update', icon: Icons.Health,     label: 'Project Update' },
    { id: 'risks',          icon: Icons.Risk,        label: 'RAID Register' },
    { id: 'slt-register',   icon: Icons.Risk,        label: 'SLT Register' },
    { id: 'change-register', icon: Icons.Tracker,   label: 'Change Register' },
    { id: 'application-catalogue', icon: Icons.AppCatalogue, label: 'Application Catalogue' },
    { id: 'ecr-tracker',    icon: Icons.Tracker,  label: 'ECR Tracker' },
    { id: 'surveys',        icon: Icons.MyTasks,  label: 'Surveys' },
    { id: 'pm-allocation', icon: Icons.Allocation,  label: 'PM Allocation' },
    { id: 'plan-templates', icon: Icons.PlanTemplate, label: 'Plan Templates' },
    { id: 'forecast',      icon: Icons.Forecast,    label: 'Resource Management' },
    { id: 'week-planner',  icon: Icons.WeekPlanner, label: 'Planner' },
    { id: 'plow',          icon: Icons.WeekPlanner, label: 'PLOW' },
  ]},
  { section: 'Procurement', items: [
    { id: 'pos',      icon: Icons.PO,       label: 'Purchase Orders' },
    { id: 'suppliers', icon: Icons.Supplier, label: 'Suppliers' },
    { id: 'delivery-addresses',  icon: Icons.PO,          label: 'Delivery Addresses' },
    { id: 'adhoc-purchases-review', icon: Icons.HWRequest, label: 'Ad-hoc Purchases' },
    { id: 'client-orders', icon: Icons.PO, label: 'Client Orders' },
  ]},
  { section: 'Hardware', items: [
    { id: 'product-catalog', icon: Icons.Catalog, label: 'Product Catalog' },
    { id: 'laptops', icon: Icons.Laptop, label: 'Laptops' },
  ]},
  { section: 'People', items: [
    { id: 'task-oversight', icon: Icons.Allocation, label: 'Task Oversight' },
    { id: 'action-oversight', icon: Icons.Allocation, label: 'Action Oversight' },
    { id: 'people',    icon: Icons.People,   label: 'People' },
    { id: 'rate-bands', icon: Icons.RateCard, label: 'Rate Bands' },
    { id: 'clearance-renewals', icon: Icons.Renew, label: 'Clearance & RDN Renewals' },
    { id: 'people-plan', icon: Icons.PlanTemplate, label: 'People & Compliance Plan' },
    { id: 'onboarding',    icon: Icons.Onboarding,  label: 'Onboarding / Offboarding' },
    { id: 'talent-acquisition', icon: Icons.Talent, label: 'Talent Acquisition' },
    { id: 'contractor-dashboard', icon: Icons.Casual,    label: 'Contractor Dashboard' },
    { id: 'resource-dashboard',  icon: Icons.Profit,     label: 'Ext. Resources Dashboard' },
    { id: 'time-overview',       icon: Icons.Timesheet,  label: 'Hours Overview' },
    { id: 'hours-analysis',      icon: Icons.Timesheet,  label: 'Hours Analysis' },
    { id: 'time-entry-audit',    icon: Icons.Timesheet,  label: 'Time Entry Audit' },
    { id: 'qms-admin',           icon: Icons.Quality,    label: 'QMS Control' },
  ]},
  { section: 'Finance', items: [
    { id: 'executive-summary', icon: Icons.Forecast,  label: 'Executive Summary' },
    { id: 'timesheet',        icon: Icons.Timesheet,  label: 'Timesheet (all staff)' },
    { id: 'payroll',          icon: Icons.Payroll,    label: 'Payroll Export' },
    { id: 'overtime-ledger',  icon: Icons.Overtime,   label: 'Overtime' },
    { id: 'email-inbox',      icon: Icons.Mail,       label: 'Accounts Inbox' },
    { id: 'profitability',    icon: Icons.Profit,     label: 'Profitability' },
    { id: 'expected-income',  icon: Icons.Income,     label: 'Expected Income' },
    { id: 'sage',       icon: Icons.Pipeline,  label: 'Sage' },
    { id: 'card-spend',       icon: Icons.RateCard,   label: 'Company Cards' },
    { id: 'vat-reclaim',      icon: Icons.Income,     label: 'VAT Reclaim' },
    { id: 'remittance',       icon: Icons.RateCard,   label: 'Remittance' },
    { id: 'po-spend',            icon: Icons.Forecast,   label: 'PO Spend Dashboard' },
    { id: 'outgoing-invoices',   icon: Icons.Forecast,   label: 'Outgoing Payments' },
    { id: 'budget',           icon: Icons.Forecast,   label: 'Budget' },
    { id: 'resource-invoices', icon: Icons.CasualInv,  label: 'Ext. Resource Invoices' },
    { id: 'audit',            icon: Icons.Audit,      label: 'Audit Trail' },
  ]},
  { section: 'Admin', items: [
    { id: 'admin',               icon: Icons.Admin,       label: 'Settings' },
    // Who reaches which page: teams, roles and people over the one grants table.
    { id: 'access',              icon: Icons.People,      label: 'Access' },
    // Item 36: this is the admin control of desks and vehicles, not a People
    // screen — the per-person booking view is My Space → Bookings.
    { id: 'bookings',            icon: Icons.Bookings,    label: 'Desk & Van Management' },
    { id: 'sow-templates',       icon: Icons.PlanTemplate, label: 'SOW Templates' },
    { id: 'clients',             icon: Icons.Clients,     label: 'Clients' },
    { id: 'repeatable',          icon: Icons.Repeatable,  label: 'Repeatable Tasks' },
    { id: 'benefits-admin',      icon: Icons.Benefits,    label: 'Benefits' },
    { id: 'overheads',           icon: Icons.Overheads,   label: 'Overheads' },
    { id: 'rate-cards',          icon: Icons.RateCard,    label: 'Rate Cards' },
    { id: 'm365-licenses',       icon: Icons.M365,        label: 'M365 Licenses' },
    { id: 'suggestions-admin',   icon: Icons.Suggestions, label: 'Suggestions' },
    { id: 'ai-usage-report',     icon: Icons.Catalog,      label: 'AI Usage Report' },
    { id: 'ai-knowledge',        icon: Icons.PlanTemplate, label: 'AI Knowledge' },
    { id: 'sent-emails-audit',   icon: Icons.Mail,         label: 'Email Management' },
    { id: 'app-adoption',        icon: Icons.Audit,        label: 'App Adoption' },
    { id: 'product-sale-history', icon: Icons.Catalog,     label: 'Product Sale History' },
    { id: 'sow-legacy-extraction', icon: Icons.Catalog,    label: 'SOW Legacy Extraction' },
    { id: 'price-book-import',   icon: Icons.Catalog,      label: 'Price Book Cutover' },
    { id: 'leave-import',        icon: Icons.Audit,        label: 'Leave Import' },
    { id: 'midwich-price-sync',  icon: Icons.Catalog,      label: 'Midwich Price Sync' },
    { id: 'backups',             icon: Icons.Audit,        label: 'Backups' },
  ]},
].map(section => ({
  // El's item 35, second pass: "include the actual lists in the main screen e.g.
  // under My Space, put Dashboard, Actions etc. into alphabetical as well."
  //
  // Sorted here rather than by rewriting the lists above, so an item added later
  // lands in the right place on its own instead of depending on whoever adds it
  // remembering.
  //
  // The SECTIONS keep their order. They are the shape of the business — My Space,
  // then the company's work in the order it flows through, then Admin — and
  // alphabetising those would open the menu on Admin and bury Delivery in the
  // middle, which is a worse list even though it is a sorted one.
  ...section,
  items: [...section.items]
    // A group sorts among the pages by its own name, and sorts what is inside
    // it by the same rule, so a page added to a group later lands in the right
    // place too.
    .map(item => (item.children
      ? { ...item, children: [...item.children].sort(byLabel) }
      : item))
    .sort(byLabel),
}));

// Every page id a section holds, groups flattened. Four places ask which
// section a page belongs to so the accordion can open it — on navigation, on
// back/forward, and on a plain hash link — and a page inside a group has to
// answer, or landing on one would leave the whole nav shut.
const sectionHasPage = (section, p) =>
  section.items.some(i => i.id === p || (i.children || []).some(c => c.id === p));

// The group a page sits in, so that opening the page opens its group.
const groupForPage = (p) => {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.children?.some(c => c.id === p)) return item.group;
    }
  }
  return null;
};

// Pages that have been split up or renamed, so a link somebody was sent before
// still opens something. #my-info was the single My Info page and landed on
// Contact Details, which is where its leave-approval emails pointed.
// Renamed or merged pages, so a bookmark or an old email link still lands
// somewhere sensible. #holidays and #overtime were separate screens until the
// two became tabs of Approvals. Pipeline and Opportunity Radar (with its BD
// Sources settings) were retired on 16 October 2026, and CRM Leads on 29
// October 2026: leads are raised on the Pipeline Tracker's Leads tab, so the
// old Pipeline and CRM Leads links open the tracker. External
// Resources folded into the Contractor Dashboard on 27 October 2026; its links
// carried a contractor's id, which the dashboard opens in its drawer.
const PAGE_ALIASES = {
  'my-info': 'my-contact', 'holidays': 'approvals', 'overtime': 'approvals',
  'pipeline': 'bid-tracker', 'crm-leads': 'bid-tracker', 'opportunity-radar': 'bid-tracker', 'bd-sources': 'bid-tracker',
  'resource-workers': 'contractor-dashboard',
};

// Does this nav entry apply to the signed-in person at all? Separate from
// canSee, which is the permission: My Benefits is a page every internal role
// holds, but only permanent staff have benefits to look at.
const navItemApplies = (item, user) => (typeof item.when === 'function' ? !!item.when(user) : true);

// Parse the page and optional detail-id from a URL hash like #sow-detail/abc123
// Where an aliased page lands INSIDE its new home. Redirecting #holidays to
// Approvals without also opening the Leave tab is half a redirect: somebody
// who bookmarked the leave queue arrives on whichever tab comes first and has
// to work out what happened to theirs.
const ALIAS_TABS = { 'holidays': 'leave', 'overtime': 'overtime' };

function parseHash(hash) {
  const parts = (hash || '').replace(/^#/, '').split('/');
  const page = parts[0] || 'dashboard';
  return { page: PAGE_ALIASES[page] || page, id: parts[1] || ALIAS_TABS[page] || null, sub: parts[2] || null };
}

// An old email link to a path such as /holidays: move the page into the hash
// before anything reads it, so /holidays opens #holidays (and so the Leave tab
// of Approvals) rather than the dashboard under a /holidays#dashboard address.
if (!IS_PORTAL_SURFACE) {
  const legacyHash = hashForLegacyPath(window.location.pathname, window.location.hash);
  if (legacyHash) window.history.replaceState(null, '', `/${window.location.search}${legacyHash}`);
}

function App() {
  // The app mounted successfully, so any prior auto-recovery reload worked —
  // clear the guard so a future stale-bundle error can self-heal once too.
  useEffect(() => { try { sessionStorage.removeItem('av-auto-reloaded'); } catch { /* storage blocked */ } }, []);

  // Initialise page/id from the URL hash so that refreshing or sharing a link
  // restores the exact view (e.g. #sow-detail/abc123).
  const [page, setPage] = useState(() => parseHash(window.location.hash).page);
  const [detailId, setDetailId] = useState(() => parseHash(window.location.hash).id);
  // Optional deep-link sub-target within a detail page (e.g. open a tab / an action)
  const [detailSub, setDetailSub] = useState(() => parseHash(window.location.hash).sub);
  const [currentUser, setCurrentUser] = useState(null);
  const [rbacModules, setRbacModules] = useState([]); // module access config from /rbac/config
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Sidebar page search — filters the nav by page name so a page can be found
  // by typing instead of by opening sections one at a time.
  const [navQuery, setNavQuery] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Header user menu, and the confirm step in front of signing out — a stray
  // click on your own name should never end the session on its own.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  // Sign out of all devices (pen test AD26341, AD8): { busy, error } while the dialog is open.
  const [signOutEverywhere, setSignOutEverywhere] = useState(null);
  const userMenuRef = useRef(null);
  const [darkMode, setDarkMode] = usePersistedState('av_dark_mode', false);
  const [density, setDensity] = usePersistedState('av_density', 'normal');
  // Track small screens so we never apply the desktop density zoom on mobile —
  // an inline `zoom` would otherwise override the stylesheet and blow the layout
  // past the viewport width.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  const contentZoom = isMobile ? 1 : (DENSITY_ZOOM[density] || 1.15);
  // Nav progress bar — must be declared here (before any early returns) to
  // satisfy React's rules of hooks.
  const [navProgress, setNavProgress] = useState(false);
  const prevPageRef = useRef(page);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const fn = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);
  // Close the user menu on an outside click or Escape.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [userMenuOpen]);
  // Start with every section collapsed except the one containing the current page.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const p = parseHash(window.location.hash).page;
    return Object.fromEntries(NAV_SECTIONS.map(s => [s.section, !sectionHasPage(s, p)]));
  });

  // A group inside a section (My Info) collapses on its own. Not an accordion:
  // there is one group today, and closing a sibling group to open this one
  // would be a rule nobody asked for.
  const [collapsedGroups, setCollapsedGroups] = useState(() => ({
    [groupForPage(parseHash(window.location.hash).page)]: false,
  }));

  const toggleGroup = (group) =>
    setCollapsedGroups(prev => ({ ...prev, [group]: !(prev[group] ?? true) }));

  const toggleSection = (section) =>
    setCollapsedSections(prev => {
      const wasCollapsed = !!prev[section];
      // Accordion: collapse all others, expand clicked (or collapse if already open)
      return Object.fromEntries(NAV_SECTIONS.map(s => [s.section, s.section === section ? !wasCollapsed : true]));
    });

  // Open actions on the current user, for the My Actions badge in the sidebar.
  // Gated on a signed-in ops person: the portals run the same shell with their
  // own session and no person behind it, so polling there would only 401.
  const openActions = useOpenActionsCount({ enabled: !IS_PORTAL_SURFACE && !!currentUser });
  // Completing something on the Actions page should take the badge down as soon
  // as you leave it, rather than on the next poll.
  const refreshOpenActions = openActions.refresh;
  useEffect(() => { refreshOpenActions(); }, [page, refreshOpenActions]);
  // The same badge on the SLT Register link: open SLT actions on the person,
  // numbers only, and only for people who can open the page.
  const sltActions = useOpenActionsCount({ enabled: !IS_PORTAL_SURFACE && !!currentUser && canAccess('slt-register'), path: 'slt-register/mine/count' });
  const refreshSltActions = sltActions.refresh;
  useEffect(() => { refreshSltActions(); }, [page, refreshSltActions]);
  // The signed-in person's own bids that need attention, for the badge on Bid
  // Tracker. Only asked for by someone the Bid Tracker is granted to.
  const bidAttention = useBidAttentionCount({ enabled: !IS_PORTAL_SURFACE && !!currentUser && canAccess('bid-tracker') });
  const refreshBidAttention = bidAttention.refresh;
  useEffect(() => { refreshBidAttention(); }, [page, refreshBidAttention]);
  // ECRs the signed-in person is PM or designer on that somebody else has
  // changed since they last opened them. The ECR Tracker announces a card being
  // opened, so the count drops at once rather than on the next poll.
  const ecrUpdates = useOpenActionsCount({ enabled: !IS_PORTAL_SURFACE && !!currentUser && canAccess('ecr-tracker'), path: 'ecr/updates/count' });
  const refreshEcrUpdates = ecrUpdates.refresh;
  useEffect(() => { refreshEcrUpdates(); }, [page, refreshEcrUpdates]);
  useEffect(() => {
    window.addEventListener('av:ecr-updates-changed', refreshEcrUpdates);
    return () => window.removeEventListener('av:ecr-updates-changed', refreshEcrUpdates);
  }, [refreshEcrUpdates]);
  // Every other page whose own tabs say something is waiting (Benefits' Change
  // Requests, the Approvals queues, Suggestions to review...) in one read, so
  // the number a tab shows also reaches its sidebar link and, folded away, its
  // section.
  const pageCounts = useNavCounts({ enabled: !IS_PORTAL_SURFACE && !!currentUser });
  const refreshPageCounts = pageCounts.refresh;
  useEffect(() => { refreshPageCounts(); }, [page, refreshPageCounts]);
  // Which sidebar items carry a badge. Collapsed sections and groups sum these.
  // A bid needing attention has no due date of its own, so it is never red;
  // nor is an ECR update.
  const navCounts = { ...pageCounts.counts, 'my-tasks': openActions, 'slt-register': sltActions, 'bid-tracker': { open: bidAttention.count, overdue: 0 }, 'ecr-tracker': ecrUpdates };

  // Load current user on mount; stamp localStorage so returning users skip the sign-in screen
  useEffect(() => {
    // Skip M365 auth for the resource/client portals and the entrance display — they have their own sessions.
    if (IS_PORTAL_SURFACE) return;
    const me = api.get('people/me')
      .then(user => {
        localStorage.setItem('av_has_visited', '1');
        setCurrentUser(user);
        loadPhotos();
      })
      .catch(() => setCurrentUser(null));
    // Module locks, and the signed-in person's own access: their principals
    // (roles, teams, self) and the grants table. Until this has arrived
    // canAccess says no to everything, so the shell waits for both before it
    // draws a menu rather than flashing an empty one.
    const access = api.get('rbac/config')
      .then(cfg => {
        setRbacModules(cfg?.modules || []);
        applyAccess({ principals: cfg?.principals || [], grants: cfg?.grants || [] });
      })
      .catch(() => setRbacModules([]));
    Promise.allSettled([me, access]).then(() => setLoading(false));
  }, []);

  // Optional navigation guard: a page (e.g. Project Update with unsaved edits)
  // can register a function that runs before any navigate(); returning false
  // (typically after its own confirm prompt) cancels the navigation.
  const navGuardRef = useRef(null);
  const registerNavGuard = useCallback((fn) => { navGuardRef.current = fn; }, []);

  // The page navigate() last sent us to. Held in a ref because navigate is
  // memoised with no dependencies, so reading `page` inside it would read
  // whatever it was on first render.
  const currentPageRef = useRef(page);
  // The id beside it, for the same reason: the hash handler is registered once
  // and needs to know whether a typed #people/<id> changed anything.
  const currentDetailRef = useRef(detailId);

  const navigate = useCallback((p, id, sub) => {
    if (navGuardRef.current && navGuardRef.current() === false) return;
    // Improvement Suggestions is a pop-out window off the header button, not a page,
    // so there is nothing to route to. The suggestion-status notification still
    // needs somewhere to land, so this opens the panel and leaves the page and
    // the history stack alone.
    if (p === 'suggestions') { setSuggestionsOpen(true); return; }
    // A renamed page resolves here as well as in parseHash, or navigate('holidays')
    // from a button or a notification link sets a page nothing renders any more.
    // Where the old page became a tab, the tab replaces the id: an id meant for the
    // old screen (a notification's request id) is not a tab of the new one.
    if (PAGE_ALIASES[p]) {
      if (ALIAS_TABS[p]) { id = ALIAS_TABS[p]; sub = undefined; }
      p = PAGE_ALIASES[p];
    }
    const hash ='#' + p + (id != null ? '/' + id : '') + (id != null && sub != null ? '/' + sub : '');
    history.pushState({ page: p, id: id ?? null, sub: sub ?? null }, '', hash);
    const changedPage = currentPageRef.current !== p;
    currentPageRef.current = p;
    if (id !== undefined) currentDetailRef.current = id ?? null;
    else if (changedPage) currentDetailRef.current = null;
    // startTransition tells React this is a non-urgent background update — it will
    // never interrupt an in-progress render to apply these state changes, which
    // eliminates the "n is not a function" concurrent-mode race during navigation.
    startTransition(() => {
      setPage(p);
      // An id belongs to the page it was opened for. Keeping it across a move to
      // a DIFFERENT page means the next page that reads detailId (the accounts
      // inbox, the product catalogue) opens whatever id happened to be left
      // over. An id-less navigate WITHIN a page still keeps the current one.
      if (id !== undefined) setDetailId(id);
      else if (changedPage) setDetailId(null);
      setDetailSub(sub ?? null);
      setSidebarOpen(false);
      // Clear the sidebar page search so the nav returns to its normal shape.
      setNavQuery('');
      const targetSection = NAV_SECTIONS.find(s => sectionHasPage(s, p));
      const targetGroup = groupForPage(p);
      if (targetGroup) setCollapsedGroups(g => ({ ...g, [targetGroup]: false }));
      if (targetSection) {
        setCollapsedSections(
          Object.fromEntries(NAV_SECTIONS.map(s => [s.section, s.section !== targetSection.section]))
        );
      }
    });
  }, []);

  // Nav progress bar + page-view tracking — fires on every navigation
  useEffect(() => {
    if (page === prevPageRef.current) return;
    prevPageRef.current = page;
    setNavProgress(true);
    const t = setTimeout(() => setNavProgress(false), 700);
    if (currentUser?.id) {
      api.post('activity/page-view', { page }).catch(() => {});
    }
    return () => clearTimeout(t);
  }, [page, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stamp initial history entry and handle browser back/forward
  useEffect(() => {
    // Not on a portal. React runs every hook before the early returns below
    // that pick a portal, so without this the staff app stamps its default
    // page onto a surface that has no pages: opening /client-portal turned
    // into /client-portal#dashboard, showing a client an address naming a
    // screen they cannot reach. The portals navigate inside themselves and
    // want the URL left exactly as it was opened.
    if (IS_PORTAL_SURFACE) return undefined;
    if (!history.state?.page) {
      history.replaceState({ page, id: detailId, sub: detailSub }, '', '#' + page + (detailId ? '/' + detailId + (detailSub ? '/' + detailSub : '') : ''));
    }
    const handlePop = (e) => {
      const s = e.state;
      if (!s?.page) return;
      currentPageRef.current = s.page;
      currentDetailRef.current = s.id || null;
      startTransition(() => {
        setPage(s.page);
        setDetailId(s.id || null);
        setDetailSub(s.sub || null);
        setSidebarOpen(false);
        const targetSection = NAV_SECTIONS.find(sec => sectionHasPage(sec, s.page));
        const targetGroup = groupForPage(s.page);
        if (targetGroup) setCollapsedGroups(g => ({ ...g, [targetGroup]: false }));
        if (targetSection) {
          setCollapsedSections(Object.fromEntries(NAV_SECTIONS.map(sec => [sec.section, sec.section !== targetSection.section])));
        }
      });
    };
    window.addEventListener('popstate', handlePop);

    // ── AND A PLAIN LINK TO A PAGE ────────────────────────────────────────────
    //
    // Navigation is pushState + popstate, and popstate does NOT fire when an <a href="#x">
    // changes the hash. So every ordinary link to another page in this app changed the
    // address bar and nothing else — "the button is clickable but leads to nowhere",
    // reported against Holidays, and equally true of "Reconnect Sage →" in the accounts
    // inbox and the resource-workers link on a contractor's record.
    //
    // Guarded against the hash we just wrote ourselves, so this does not double-handle a
    // pushState navigation.
    const handleHash = () => {
      const { page: p, id, sub } = parseHash(window.location.hash);
      // The same page under a different id, such as #people/<id> edited by
      // hand: only the id moves, and the page stays mounted to act on it.
      // Compared as text, since a navigate() id may be a number and the hash
      // never is.
      if (p && p === currentPageRef.current && String(id ?? '') !== String(currentDetailRef.current ?? '')) {
        currentDetailRef.current = id || null;
        history.replaceState({ page: p, id: id ?? null, sub: sub ?? null }, '',
          '#' + p + (id ? '/' + id + (sub ? '/' + sub : '') : ''));
        startTransition(() => {
          setDetailId(id || null);
          setDetailSub(sub || null);
        });
        return;
      }
      if (!p || p === currentPageRef.current) return;
      currentPageRef.current = p;
      currentDetailRef.current = id || null;
      // Written back under the page's own name rather than whatever was typed,
      // so following an aliased link (#my-info) leaves a bookmarkable address
      // for the page that actually opened.
      history.replaceState({ page: p, id: id ?? null, sub: sub ?? null }, '',
        '#' + p + (id ? '/' + id + (sub ? '/' + sub : '') : ''));
      startTransition(() => {
        setPage(p);
        setDetailId(id || null);
        setDetailSub(sub || null);
        setSidebarOpen(false);
        const targetSection = NAV_SECTIONS.find(sec => sectionHasPage(sec, p));
        const targetGroup = groupForPage(p);
        if (targetGroup) setCollapsedGroups(g => ({ ...g, [targetGroup]: false }));
        if (targetSection) {
          setCollapsedSections(Object.fromEntries(NAV_SECTIONS.map(sec => [sec.section, sec.section !== targetSection.section])));
        }
      });
    };
    window.addEventListener('hashchange', handleHash);

    return () => {
      window.removeEventListener('popstate', handlePop);
      window.removeEventListener('hashchange', handleHash);
    };
  }, []);

  // Dedicated portals — completely separate auth, render immediately
  if (IS_RESOURCE_PORTAL) return <CasualPortal />;
  if (IS_CLIENT_PORTAL) {
    return <ClientPortalShell TimesheetApprovals={(props) => <ClientPortal {...props} embedded />} />;
  }
  if (IS_DISPLAY_SURFACE) return <FloorPlanDisplayShell />;

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat', color: '#6B7280' }}>Loading...</div>;
  }

  // Not authenticated — returning users auto-redirect, new browsers see the sign-in button
  if (!currentUser) {
    if (localStorage.getItem('av_has_visited')) {
      window.location.href = '/auth/login';
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat', background: '#0D1D32', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Signing you in…</div>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat', background: '#0D1D32', color: '#fff' }}>
        <img src="/av-logo-white.png" alt="Alton Valley" style={{ height: 48, width: 'auto', objectFit: 'contain' }} />
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>Business Operations Platform</p>
        <a
          href="/auth/login"
          onClick={() => localStorage.setItem('av_has_visited', '1')}
          style={{ marginTop: 24, padding: '8px 24px', background: '#A1723D', color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}
        >
          Sign in with Microsoft
        </a>
        <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.15)', marginTop: 16 }}>Requires Entra ID authentication via Cloudflare Access</p>
      </div>
    );
  }

  // ?bulkscan=1: the bulk scan basket, full screen with the camera in the page.
  if (BULK_SCAN_MODE) {
    return (
      <ToastProvider>
        <AppContext.Provider value={{ currentUser, navigate: () => {}, page: 'bulkscan', detailId: null }}>
          <BulkScanPage />
          <ToastContainer />
        </AppContext.Provider>
      </ToastProvider>
    );
  }

  // If the URL contains ?scan=AV-INV-XXXXX, show the mobile scan page (full screen, no sidebar).
  // An active bulk scan basket takes the scan first, then an active stock take
  // counts it, otherwise it opens the single item.
  if (SCAN_TAG) {
    return (
      <ToastProvider>
        <AppContext.Provider value={{ currentUser, navigate: () => {}, page: 'scan', detailId: null }}>
          {BULK_SCAN_ACTIVE ? <BulkScanPage scannedTag={SCAN_TAG} />
            : ACTIVE_STOCKTAKE
            ? <StockTakeScanMode stId={ACTIVE_STOCKTAKE} scannedTag={SCAN_TAG} />
            : <ScanPage assetTag={SCAN_TAG} />}
          <ToastContainer />
        </AppContext.Provider>
      </ToastProvider>
    );
  }

  // If the URL contains ?stocktake=<id>, show the full-screen stock-take scan page
  if (STOCKTAKE_ID) {
    return (
      <ToastProvider>
        <AppContext.Provider value={{ currentUser, navigate: () => {}, page: 'stocktake', detailId: null }}>
          <StockTakeScanPage stockTakeId={STOCKTAKE_ID} review={STOCKTAKE_REVIEW} />
          <ToastContainer />
        </AppContext.Provider>
      </ToastProvider>
    );
  }

  const userRole = currentUser?.user_role || 'engineer';
  // The header shows who you are (name, job title); the roles you hold live in the account
  // dropdown. See frontend/src/utils/identity.js for why.
  const jobLine = userJobLine(currentUser);
  const detailLine = userDetailLine(currentUser);
  const accessLine = userAccessLine(userRole);
  // Not named `initials`: that is already imported from utils/format at the top of the file.
  const avatarInitials = userInitials(currentUser);
  // The same colour the person's avatar has everywhere else on the platform.
  const avatarColour = currentUser?.avatar_color || '#0D1D32';
  const canSee = (pageId) => canAccess(pageId);
  // Module-level lock for the page currently being viewed (null = not locked).
  const moduleLock = moduleLockInfo(page, rbacModules, currentUser);

  // ?display=floor-plan: the office plan on a wall, full screen, nothing else on it.
  // Placed here rather than with the scan modes above because it is the one full-screen
  // surface that asks a page grant, and canSee is only defined from this point.
  if (DISPLAY_VIEW === 'floor-plan') {
    if (!canSee('floor-plan-display')) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat', background: '#0D1D32', color: '#fff', textAlign: 'center', padding: 24 }}>
          <img src="/av-logo-white.png" alt="Alton Valley" style={{ height: 40, width: 'auto', objectFit: 'contain' }} />
          <p style={{ fontSize: 13, fontWeight: 600, marginTop: 20 }}>This sign-in cannot show the floor plan display.</p>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>Ask an administrator to grant "Floor Plan Display" to its role.</p>
        </div>
      );
    }
    return <FloorPlanDisplayPage locationId={DISPLAY_LOCATION} />;
  }

  return (
    <ToastProvider>
    <AppContext.Provider value={{ currentUser, navigate, page, detailId, detailSub, registerNavGuard }}>
      <div className="app">
        {/* Mobile overlay */}
        {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <div className="sidebar-logo" onClick={() => navigate('dashboard')} style={{ cursor: 'pointer' }} title="Go to dashboard">
            <AVLogo />
          </div>

          {/* Page search — filters the nav below by page name */}
          <div className="nav-search" style={{ padding: '8px 8px 4px', position: 'relative' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ width: 12, height: 12, color: 'rgba(255,255,255,0.35)', position: 'absolute', left: 16, top: '50%', transform: 'translateY(-1px)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setNavQuery(''); }}
              placeholder="Search pages…"
              aria-label="Search pages"
              style={{
                width: '100%', padding: '5px 8px 5px 24px', fontSize: 10, fontFamily: 'inherit',
                color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.10)', borderRadius: 4, outline: 'none',
              }}
            />
          </div>

          <div className="sidebar-nav">
            {filterNavSections(NAV_SECTIONS, navQuery).map(section => {
              // A group is only ever a container, never a second place where
              // access is decided: it shows exactly when a page inside it does.
              const visible = section.items
                .map(i => (i.children
                  ? { ...i, children: i.children.filter(c => canSee(c.id) && navItemApplies(c, currentUser)) }
                  : i))
                .filter(i => (i.children ? i.children.length > 0 : canSee(i.id) && navItemApplies(i, currentUser)));
              if (visible.length === 0) return null;
              // While a search is live every matching section is open, whatever
              // its accordion state was, or the results would be invisible.
              const isCollapsed = navQuery.trim() ? false : !!collapsedSections[section.section];
              const collapsed = isCollapsed;
              return (
                <div key={section.section}>
                  <div
                    className="nav-section-label"
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
                    onClick={() => toggleSection(section.section)}
                  >
                    <span>{section.section}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {collapsed && <NavBadge counts={sumNavBadges(visible, navCounts)} noun="item" />}
                      <span style={{ fontSize: '7px', opacity: 0.5, marginRight: '2px' }}>{collapsed ? '▶' : '▼'}</span>
                    </span>
                  </div>
                  {!collapsed && visible.map(item => {
                    const NavIcon = item.icon;
                    if (item.children) {
                      // A search opens every group it matched, for the same
                      // reason it opens every section: otherwise the results
                      // are filtered down to something invisible.
                      const groupShut = navQuery.trim() ? false : (collapsedGroups[item.group] ?? true);
                      return (
                        <div key={item.group}>
                          <div
                            className={`nav-item nav-group ${item.children.some(c => c.id === page) ? 'active' : ''}`}
                            onClick={() => toggleGroup(item.group)}
                          >
                            {typeof NavIcon === 'function' && <NavIcon />}
                            <span>{item.label}</span>
                            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                              {groupShut && <NavBadge counts={sumNavBadges(item.children, navCounts)} noun="item" />}
                              <span style={{ fontSize: '7px', opacity: 0.5 }}>{groupShut ? '▶' : '▼'}</span>
                            </span>
                          </div>
                          {!groupShut && item.children.map(child => {
                            const ChildIcon = child.icon;
                            const childLocked = !!moduleLockInfo(child.id, rbacModules, currentUser);
                            return (
                              <div
                                key={child.id}
                                className={`nav-item nav-item-child ${page === child.id ? 'active' : ''}`}
                                onClick={() => navigate(child.id)}
                                title={childLocked ? `${child.label} — coming soon` : undefined}
                              >
                                {typeof ChildIcon === 'function' && <ChildIcon />}
                                <span style={childLocked ? { opacity: 0.55 } : undefined}>{child.label}</span>
                                <NavBadge counts={navCounts[child.id]} noun="item" title={navCounts[child.id]?.title} />
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                    const locked = !!moduleLockInfo(item.id, rbacModules, currentUser);
                    return (
                      <div
                        key={item.id}
                        className={`nav-item ${page === item.id ? 'active' : ''}`}
                        onClick={() => navigate(item.id)}
                        title={locked ? `${item.label} — coming soon` : undefined}
                      >
                        {typeof NavIcon === 'function' && <NavIcon />}
                        <span style={locked ? { opacity: 0.55 } : undefined}>{item.label}</span>
                        <NavBadge counts={navCounts[item.id]} noun={item.id === 'slt-register' ? 'SLT action' : 'action'}
                          title={item.id === 'bid-tracker' ? `${bidAttention.count} of your bids ${bidAttention.count === 1 ? 'needs' : 'need'} attention`
                            : item.id === 'ecr-tracker' ? `${ecrUpdates.open} of your ECRs ${ecrUpdates.open === 1 ? 'has' : 'have'} changed since you last opened ${ecrUpdates.open === 1 ? 'it' : 'them'}`
                            : navCounts[item.id]?.title} />
                        {locked && (
                          <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 'auto', opacity: 0.5 }}>
                            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {navQuery.trim() && filterNavSections(NAV_SECTIONS, navQuery).filter(s => s.items.some(i => (i.children || [i]).some(c => canSee(c.id) && navItemApplies(c, currentUser)))).length === 0 && (
              <div style={{ padding: '12px 8px', fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                No pages match “{navQuery.trim()}”
              </div>
            )}
          </div>

          {/* Quick Time Log — docked in the sidebar dead-space, above the version stamp */}
          <div style={{ padding: '4px 6px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <ShellErrorBoundary fallback={null}>
              <QuickTimeLog user={currentUser} variant="sidebar" />
            </ShellErrorBoundary>
          </div>

          {/* Version stamp */}
          <div style={{ padding: '4px 8px 6px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            {buildInfo.build > 0 ? (
              <span
                style={{ fontSize: 11, fontFamily: 'monospace', color: '#E5B87A', fontWeight: 700, letterSpacing: '0.03em' }}
                title={buildInfo.gitHash ? `commit ${buildInfo.gitHash}` : `build ${buildInfo.build}`}
              >
                build #{buildInfo.build}
              </span>
            ) : (
              <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'rgba(255,255,255,0.2)' }}>dev</span>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="main">
          <div className="header">
            <button className="hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <div style={{ marginLeft: 8 }}>
              <ShellErrorBoundary fallback={null}>
                <GlobalSearch onNavigate={navigate} />
              </ShellErrorBoundary>
            </div>
            <div style={{ flex: 1 }} />
            {/* S/M/L density */}
            <div style={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden', marginRight: 6 }} className="hidden-mobile">
              {[['compact','S'],['normal','M'],['comfortable','L']].map(([key, label]) => (
                <button key={key} onClick={() => setDensity(key)}
                  title={{ compact: 'Compact', normal: 'Normal', comfortable: 'Comfortable' }[key]}
                  style={{ padding: '2px 6px', fontSize: 9, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', border: 'none', transition: 'all 0.1s',
                    background: density === key ? '#0D1D32' : 'transparent',
                    color: density === key ? '#fff' : '#6B7280' }}>
                  {label}
                </button>
              ))}
            </div>
            {/* Dark mode */}
            <button onClick={() => setDarkMode(v => !v)} title={darkMode ? 'Light mode' : 'Dark mode'}
              style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', marginRight: 6, color: '#6B7280', flexShrink: 0 }}>
              {darkMode ? <Icons.Sun /> : <Icons.Moon />}
            </button>
            <button
              onClick={() => setSuggestionsOpen(true)}
              title="Improvement Suggestions"
              style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', marginRight: 6, color: '#6B7280', flexShrink: 0 }}
            >
              <Icons.Lightbulb />
            </button>
            <ShellErrorBoundary fallback={null}>
              <NotificationBell onNavigate={navigate} />
            </ShellErrorBoundary>
            <div style={{ position: 'relative', marginLeft: 8 }} className="hidden-mobile" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                title="Account"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', padding: '2px 4px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {/* Inline colours, so the .dark rules in styles.css cannot reach them:
                    the light-mode grey is near-invisible on the dark header band. */}
                <Avatar personId={currentUser?.id} as="span" style={{ width: 22, height: 22, borderRadius: '50%', background: avatarColour, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                  {avatarInitials}
                </Avatar>
                <span style={{ textAlign: 'left' }}>
                  <span style={{ display: 'block', fontSize: 9, fontWeight: 600, color: darkMode ? 'rgb(226 232 240)' : '#374151', whiteSpace: 'nowrap' }}>
                    {currentUser?.first_name} {currentUser?.last_name}
                  </span>
                  {/* No job title recorded: no second line at all, rather than an empty one. */}
                  {jobLine && (
                    <span style={{ display: 'block', fontSize: 8, fontWeight: 400, color: darkMode ? 'rgb(148 163 184)' : '#9CA3AF', whiteSpace: 'nowrap' }}>
                      {jobLine}
                    </span>
                  )}
                </span>
                <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke={darkMode ? 'rgb(148 163 184)' : '#9CA3AF'} strokeWidth="2.5" style={{ flexShrink: 0, transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.12s' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {userMenuOpen && (
                <div
                  role="menu"
                  style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, minWidth: 200, background: darkMode ? 'rgb(30 41 59)' : '#fff', border: `1px solid ${darkMode ? 'rgb(51 65 85)' : '#E5E7EB'}`, borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 100, overflow: 'hidden' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: `1px solid ${darkMode ? 'rgb(51 65 85)' : '#E5E7EB'}` }}>
                    <Avatar personId={currentUser?.id} as="span" style={{ width: 28, height: 28, borderRadius: '50%', background: avatarColour, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                      {avatarInitials}
                    </Avatar>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: darkMode ? 'rgb(226 232 240)' : '#0D1D32' }}>
                        {currentUser?.first_name} {currentUser?.last_name}
                      </span>
                      {/* Job title and team, whichever of them there is. */}
                      {detailLine && (
                        <span style={{ display: 'block', fontSize: 9, color: darkMode ? 'rgb(148 163 184)' : '#6B7280' }}>
                          {detailLine}
                        </span>
                      )}
                      {/* The people record's work address is email_work; the session user
                          carries plain email. Whichever is there. */}
                      {(currentUser?.email_work || currentUser?.email) && (
                        <span style={{ display: 'block', fontSize: 9, color: darkMode ? 'rgb(148 163 184)' : '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {currentUser.email_work || currentUser.email}
                        </span>
                      )}
                    </span>
                  </div>
                  {accessLine && (
                    <div style={{ padding: '7px 10px', borderBottom: `1px solid ${darkMode ? 'rgb(51 65 85)' : '#E5E7EB'}` }}>
                      <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgb(148 163 184)' : '#9CA3AF', marginBottom: 2 }}>Access</div>
                      <div style={{ fontSize: 9, color: darkMode ? 'rgb(226 232 240)' : '#374151' }}>{accessLine}</div>
                    </div>
                  )}
                  {currentUser?.last_login && (
                    <div style={{ padding: '6px 10px', borderBottom: `1px solid ${darkMode ? 'rgb(51 65 85)' : '#E5E7EB'}`, fontSize: 8, color: darkMode ? 'rgb(148 163 184)' : '#9CA3AF' }}>
                      Last login: {new Date(currentUser.last_login).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  )}
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); setLogoutConfirmOpen(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', color: darkMode ? 'rgb(226 232 240)' : '#374151', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Log out
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); setSignOutEverywhere({ busy: false, error: '' }); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', color: darkMode ? 'rgb(226 232 240)' : '#374151', background: 'transparent', border: 'none', borderTop: `1px solid ${darkMode ? 'rgb(51 65 85)' : '#E5E7EB'}`, cursor: 'pointer' }}
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="4" width="14" height="10" rx="1" /><rect x="17" y="8" width="5" height="10" rx="1" /><line x1="6" y1="18" x2="12" y2="18" />
                    </svg>
                    Sign out of all devices
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="content" style={{ zoom: contentZoom }}>
            {/* Navigation progress bar — thin gold stripe at top while page is loading */}
            {navProgress && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 999, pointerEvents: 'none' }}>
                <div style={{
                  height: '100%', background: '#b5a14a',
                  animation: 'nav-progress 0.7s ease-out forwards',
                }} />
              </div>
            )}
            <PageErrorBoundary resetKey={page}>
              {moduleLock && <ModuleLockedPanel info={moduleLock} />}
              {!moduleLock && <>
              {page === 'crm-dashboard' && canSee('crm-dashboard') && <CrmDashboardPage onNavigate={navigate} />}
              {page === 'crm-contacts'  && canSee('crm-contacts')  && <CrmContactsPage />}
              {page === 'sage'          && canSee('sage')          && <SagePage />}
              {/* Company card spend. Finance get the whole thing; everybody else reaches
                  their own transactions from My Space, because the receipt is theirs to
                  upload and nobody else can do it for them. */}
              {page === 'card-spend'    && canSee('card-spend')    && <CardSpendPage currentUser={currentUser} transactionId={detailId} />}
              {/* mineOnly is what makes this a DIFFERENT page rather than the same one at
                  another address. Without it, anybody holding a finance role opened "My
                  Company Card" and got the whole company's card spend — the finance screen,
                  under a personal heading. */}
              {page === 'my-card-spend' && <CardSpendPage currentUser={currentUser} mineOnly />}
              {page === 'bid-tracker'   && canSee('bid-tracker')   && <BidTrackerPage />}
              {page === 'bid-detail'    && canSee('bid-tracker')   && <BidDetailPage />}
              {page === 'bid-library'   && canSee('bid-library')   && <BidLibraryPage />}
              {page === 'dashboard' && (userRole.split(',').map(r => r.trim()).includes('inventory-manager') && !userRole.split(',').map(r => r.trim()).some(r => ['admin','pm','pmo','finance','billing','hr','partner','commercial-lead','bd'].includes(r)) ? <InventoryManagerDashboard /> : <Dashboard />)}
              {page === 'tracker' && canSee('tracker') && <TrackerPage />}
              {page === 'draft-sows' && <DraftSowDashboard />}
              {page === 'my-sows' && <MySowsPage />}
              {page === 'sow-detail' && <SOWDetailPage />}
              {page === 'sow-overview' && <SowOverviewPage />}
              {page === 'milestones' && canSee('milestones') && <MilestonesPage />}
              {page === 'sow-validation' && canSee('sow-validation') && <SOWValidationPage />}
              {page === 'sow-reconciliation' && canSee('sow-reconciliation') && <ReconciliationPage />}
              {page === 'service-expiry' && canSee('service-expiry') && <ServiceExpiryPage />}
              {page === 'support-sows' && canSee('support-sows') && <SupportSowsPage />}
              {page === 'pmo-dashboard' && canSee('pmo-dashboard') && <PmoDashboardPage />}
              {page === 'delivery-analytics' && canSee('delivery-analytics') && <DeliveryAnalyticsPage />}
              {page === 'pmo-portfolio'   && canSee('pmo-portfolio')   && <PmoPortfolioPage />}
              {page === 'plan-portfolio'  && canSee('plan-portfolio')  && <PlanPortfolioPage />}
              {page === 'pm-achievements' && canSee('pm-achievements') && <PmAchievementPage />}
              {page === 'delivery-cockpit' && canSee('delivery-cockpit') && <DeliveryCockpitPage />}
              {page === 'project-update' && canSee('project-update') && <ProjectUpdatePage />}
              {page === 'risks' && canSee('risks') && <RiskRegisterPage />}
              {page === 'slt-register' && canSee('slt-register') && <SltRegisterPage />}
              {page === 'change-register' && canSee('change-register') && <ChangeRegisterPage />}
              {page === 'application-catalogue' && canSee('application-catalogue') && <ApplicationCataloguePage initialApplicationId={detailId} />}
              {page === 'ecr-tracker' && canSee('ecr-tracker') && <EcrTrackerPage initialEcrId={detailId} />}
              {page === 'surveys' && canSee('surveys') && <SurveysPage initialSurveyId={detailId} />}
              {page === 'pm-allocation' && canSee('pm-allocation') && <PmAllocationPage />}
              {page === 'repeatable' && canSee('repeatable') && <RepeatablePage />}
              {page === 'forecast' && canSee('forecast') && <ForecastPage />}
              {page === 'week-planner' && canSee('week-planner') && <WeekPlannerPage />}
              {page === 'plow' && canSee('plow') && <PlowPage initialPlowId={detailId} />}
              {page === 'pos' && canSee('pos') && <POPage />}
              {page === 'inventory' && canSee('inventory') && <InventoryPage />}
              {page === 'hardware-requests' && canSee('hardware-requests') && <HardwareRequestPage />}
              {page === 'adhoc-purchases-review' && canSee('adhoc-purchases-review') && <AdhocPurchasesReviewPage />}
              {page === 'client-orders' && canSee('client-orders') && <ClientOrdersPage />}
              {page === 'portal-catalogue' && canSee('portal-catalogue') && <PortalCataloguePage />}
              {page === 'my-contact'    && canSee('my-contact')    && <MyContactPage />}
              {page === 'my-leave'      && canSee('my-leave')      && <MyLeavePage />}
              {page === 'my-benefits'   && canSee('my-benefits')   && <MyBenefitsPage />}
              {page === 'my-allowances' && canSee('my-allowances') && <MyAllowancesPage />}
              {page === 'my-tasks' && canSee('my-tasks') && <MyTasksPage />}
              {page === 'my-time'  && canSee('my-time')  && <MyTimePage />}
              {page === 'my-list'  && canSee('my-list')  && <MyListPage />}
              {page === 'task-oversight' && canSee('task-oversight') && <TaskOversightPage />}
              {page === 'action-oversight' && canSee('action-oversight') && <ActionOversightPage />}
              {page === 'people' && canSee('people') && <PeoplePage initialPersonId={detailId} />}
              {page === 'rate-bands' && canSee('rate-bands') && <RateBandsPage />}
              {page === 'clearance-renewals' && canSee('clearance-renewals') && <ClearanceRenewalsPage tab={detailId} />}
              {page === 'laptops' && canSee('laptops') && <LaptopsPage initialPersonId={detailId} />}
              {page === 'people-plan' && canSee('people-plan') && <PeoplePlanPage tab={detailId} />}
              {page === 'org-chart' && canSee('org-chart') && <OrgChartPage />}
              {/* The tab comes from the address, so #approvals/leave opens on
                  leave and the aliased #holidays lands there too. Each tab
                  re-asks its own gate inside the page. */}
              {page === 'approvals' && canSee('approvals') && <ApprovalsPage tab={detailId} />}
              {page === 'leave-calendar' && canSee('leave-calendar') && <LeaveCalendarPage />}
              {page === 'onboarding'     && canSee('onboarding')     && <OnboardingPage />}
              {page === 'talent-acquisition' && canSee('talent-acquisition') && <TalentAcquisitionPage />}
              {/* No canSee: an interviewer reaches this from their Action or the invite email, and
                  being on the panel is the access. The API returns 404 to anyone else. */}
              {page === 'interview-feedback' && <InterviewFeedbackPage interviewId={detailId} />}
              {page === 'my-bookings'     && <MyBookingsPage user={currentUser} initialLocationId={detailId} />}
              {page === 'bookings'        && canSee('bookings')        && <ResourceManagementPage user={currentUser} />}
              {page === 'suggestions-admin' && canSee('suggestions-admin') && <SuggestionsAdminPage user={currentUser} />}
              {page === 'sent-emails-audit' && canSee('sent-emails-audit') && <SentEmailsPage />}
              {page === 'resource-dashboard' && canSee('resource-dashboard') && <ResourcesDashboardPage />}
              {page === 'data-import'      && canSee('data-import')      && <ImportPage />}
              {page === 'sow-document-backfill' && canSee('sow-document-backfill') && <SowDocumentBackfillPage />}
              {page === 'zoho-cutover-import' && canSee('zoho-cutover-import') && <ZohoCutoverImportPage />}
              {page === 'price-book-import' && canSee('price-book-import') && <PriceBookImportPage />}
              {page === 'leave-import' && canSee('leave-import') && <LeaveImportPage />}
              {page === 'midwich-price-sync' && canSee('midwich-price-sync') && <MidwichPriceSyncPage />}
              {page === 'product-sale-history' && canSee('product-sale-history') && <ProductSaleHistoryPage />}
              {page === 'sow-legacy-extraction' && canSee('sow-legacy-extraction') && <SowLegacyExtractionPage />}
              {page === 'backups' && canSee('backups') && <BackupsPage />}
              {page === 'sow-document-duplicates' && canSee('sow-document-duplicates') && <SowDocumentDuplicatesPage />}
              {page === 'ai-usage-report'  && canSee('ai-usage-report')  && <AIUsageReportPage />}
              {page === 'app-adoption'     && canSee('app-adoption')     && <AppAdoptionPage />}
              {page === 'qms-library'     && canSee('qms-library')     && <QmsLibraryPage />}
              {page === 'qms-admin'       && canSee('qms-admin')       && <QmsAdminPage />}
              {page === 'budget' && canSee('budget') && <BudgetPage />}
              {page === 'executive-summary' && canSee('executive-summary') && <ExecutiveSummaryPage />}
              {page === 'timesheet' && canSee('timesheet') && <TimesheetPage />}
              {page === 'payroll' && canSee('payroll') && <PayrollPage />}
              {page === 'overtime-ledger' && canSee('overtime-ledger') && <OvertimeLedgerPage />}
              {page === 'profitability' && canSee('profitability') && <ProfitabilityPage user={currentUser} />}
              {page === 'expected-income' && canSee('expected-income') && <ExpectedIncomePage />}
              {page === 'remittance'      && canSee('remittance')      && <RemittancePage />}
              {page === 'po-spend'           && canSee('po-spend')           && <POSpendPage />}
              {page === 'outgoing-invoices'  && canSee('outgoing-invoices')  && <OutgoingInvoicesPage />}
              {page === 'audit' && canSee('audit') && <AuditPage />}
              {page === 'admin' && canSee('admin') && <AdminPage />}
              {page === 'access' && canSee('access') && <AccessPage />}
              {page === 'clients' && canSee('clients') && <ClientsPage />}
              {page === 'benefits-admin' && canSee('benefits-admin') && <BenefitsAdminPage />}
              {page === 'overheads'   && canSee('overheads')   && <OverheadsPage />}
              {page === 'rate-cards'  && canSee('rate-cards')  && <RateCardPage />}
              {page === 'suppliers'   && canSee('suppliers')   && <SupplierPage />}
              {page === 'product-catalog' && canSee('product-catalog') && <ProductCatalogPage initialProductId={detailId} />}
              {page === 'plan-templates'  && canSee('plan-templates')  && <PlanTemplatesPage />}
              {page === 'sow-templates'  && canSee('sow-templates')  && <SOWTemplatePage />}
              {page === 'ai-knowledge'   && canSee('ai-knowledge')   && <AiKnowledgePage />}
              {page === 'm365-licenses'  && canSee('m365-licenses')  && <M365LicensesPage />}
              {page === 'email-inbox'      && canSee('email-inbox')      && <EmailInboxPage initialEmailId={detailId} />}
              {page === 'vat-reclaim'      && canSee('vat-reclaim')      && <VatReclaimPage />}
              {page === 'contractor-dashboard' && canSee('contractor-dashboard') && <ContractorDashboardPage />}
              {page === 'resource-invoices'  && canSee('resource-invoices')  && <CasualInvoicesPage invoiceId={detailId} />}
              {page === 'delivery-addresses' && canSee('delivery-addresses') && <DeliveryAddressPage />}
              {page === 'time-overview'      && canSee('time-overview')      && <TimeOverviewPage />}
              {page === 'hours-analysis'     && canSee('hours-analysis')     && <HoursAnalysisPage />}
              {page === 'time-entry-audit'   && canSee('time-entry-audit')   && <TimeEntryAuditPage />}
              </>}
            </PageErrorBoundary>
          </div>
        </div>
      </div>
      <ShellErrorBoundary fallback={null}>
        {suggestionsOpen && <SuggestionsPanel zoom={contentZoom} onClose={() => setSuggestionsOpen(false)} />}
      </ShellErrorBoundary>
      {/* App-level dialogs render beside .content, so take its zoom to follow the S / M / L switch too. */}
      <div style={{ zoom: contentZoom }}>
      {logoutConfirmOpen && (
        <Modal
          title="Log out"
          onClose={() => setLogoutConfirmOpen(false)}
          footer={
            <div className="flex gap-2 justify-end w-full">
              <button onClick={() => setLogoutConfirmOpen(false)} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
              {/* A full navigation, not a fetch: /auth/logout clears the session
                  cookie and hands over to Microsoft to end the sign-in. */}
              <button
                onClick={() => {
                  // Drop the returning-user flag, otherwise coming back lands on
                  // the silent re-login path instead of the sign-in screen.
                  try { localStorage.removeItem('av_has_visited'); } catch { /* storage blocked */ }
                  window.location.href = '/auth/logout';
                }}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded"
              >
                Log out
              </button>
            </div>
          }
        >
          <p className="text-[11px] text-gray-600">Are you sure you want to log out?</p>
        </Modal>
      )}
      {signOutEverywhere && (
        <Modal
          title="Sign out of all devices"
          onClose={() => { if (!signOutEverywhere.busy) setSignOutEverywhere(null); }}
          footer={
            <div className="flex gap-2 justify-end w-full">
              <button
                onClick={() => setSignOutEverywhere(null)}
                disabled={signOutEverywhere.busy}
                className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white disabled:opacity-50"
              >
                Stay signed in
              </button>
              <button
                onClick={async () => {
                  setSignOutEverywhere({ busy: true, error: '' });
                  try {
                    await api.post('sessions/end-all', {});
                  } catch (e) {
                    setSignOutEverywhere({ busy: false, error: e.message || 'Ops could not sign you out everywhere. Try again.' });
                    return;
                  }
                  // Every session has ended, this one included: finish signing
                  // this browser out the ordinary way.
                  try { localStorage.removeItem('av_has_visited'); } catch { /* storage blocked */ }
                  window.location.href = '/auth/logout';
                }}
                disabled={signOutEverywhere.busy}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-50"
              >
                {signOutEverywhere.busy ? 'Signing out…' : 'Sign out everywhere'}
              </button>
            </div>
          }
        >
          <div className="space-y-2 text-[11px] text-gray-600">
            <p>
              This ends your Ops sessions on every computer, phone and browser, including this one.
              Use it if you signed in somewhere you no longer use, or think somebody else could be using your session.
            </p>
            <p>Each device will need to sign in to Ops again before it can be used.</p>
            {signOutEverywhere.error && (
              <p role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1.5">{signOutEverywhere.error}</p>
            )}
          </div>
        </Modal>
      )}
      {/* Mounted once for the whole app rather than on the pages that
          changed: somebody looking for a page that has moved cannot be told
          on the page they cannot find. It shows only to people who were
          here before a change, and only until they have read it. */}
      <WhatsChanged />
      </div>
      <ToastContainer />
    </AppContext.Provider>
    </ToastProvider>
  );
}

export default App;
